import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Form,
  FormField,
  Header,
  Input,
  Multiselect,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  Textarea,
} from "@amzn/awsui-components-react";
import { MultiselectProps } from "@amzn/awsui-components-react/polaris/multiselect";
import Page from "components/page";
import { useAppSelector } from "app/store";
import { useNotifications } from "features/notifications";
import {
  KBDocumentInput,
  useSubmitKBApprovalMutation,
} from "pages/gaia-ai/kb-approval-endpoints";
import { useUserNameSelector } from "hooks/useNameSelector";
import {
  ApprovalRouteState,
  DeletionApprovalRouteState,
  ExistingDocApprovalRouteState,
} from "./definitions";

const isDeletionState = (
  s:
    | ApprovalRouteState
    | DeletionApprovalRouteState
    | ExistingDocApprovalRouteState
    | null,
): s is DeletionApprovalRouteState => !!s && "atlasObjectKeys" in s;

const isExistingDocState = (
  s:
    | ApprovalRouteState
    | DeletionApprovalRouteState
    | ExistingDocApprovalRouteState
    | null,
): s is ExistingDocApprovalRouteState => !!s && "existingDocumentIds" in s;

const getFilenameFromKey = (key: string): string =>
  key.substring(key.lastIndexOf("/") + 1);

const ApproverSelectionPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const routeState = location.state as
    | ApprovalRouteState
    | DeletionApprovalRouteState
    | ExistingDocApprovalRouteState
    | null;

  const isDeletion = isDeletionState(routeState);
  const isExistingDoc = isExistingDocState(routeState);
  const fileSizes = isDeletionState(routeState) ? routeState.fileSizes : {};
  const fileNames = isExistingDocState(routeState) ? routeState.fileNames : {};
  const teamName = routeState?.teamName ?? "";

  const criticalOwnerOptions = useAppSelector(
    (state) => state.uiContent.criticalOwnerOptions,
  );
  const { userName } = useUserNameSelector();
  const today = new Date().toLocaleDateString("en-CA");
  const defaultTitle = isDeletion
    ? `KB Deletion Approval - ${userName} - ${today}`
    : `KB Approval - ${userName} - ${today}`;

  const [submitKBApproval, { isLoading: isSubmitting }] =
    useSubmitKBApprovalMutation();

  const [files, setFiles] = useState<KBDocumentInput[]>(
    routeState &&
      !isDeletionState(routeState) &&
      !isExistingDocState(routeState)
      ? routeState.files
      : [],
  );
  const [atlasObjectKeys, setAtlasObjectKeys] = useState<string[]>(
    isDeletionState(routeState) ? routeState.atlasObjectKeys : [],
  );
  const [existingDocumentIds, setExistingDocumentIds] = useState<string[]>(
    isExistingDocState(routeState) ? routeState.existingDocumentIds : [],
  );
  const [selectedApprovers, setSelectedApprovers] = useState<
    MultiselectProps.Option[]
  >([]);
  const [approvalMode, setApprovalMode] = useState<"AND" | "OR">("AND");
  const [message, setMessage] = useState("");
  const [reviewTitle, setReviewTitle] = useState(defaultTitle);

  useEffect(() => {
    if (isExistingDoc) {
      if (!existingDocumentIds.length) navigate("/gaia-ai", { replace: true });
    } else if (isDeletion) {
      if (!atlasObjectKeys.length) navigate("/gaia-ai", { replace: true });
    } else {
      if (!files.length) navigate("/gaia-ai", { replace: true });
    }
  }, [
    files.length,
    atlasObjectKeys.length,
    existingDocumentIds.length,
    navigate,
    isDeletion,
    isExistingDoc,
  ]);

  if (
    isExistingDoc
      ? !existingDocumentIds.length
      : isDeletion
        ? !atlasObjectKeys.length
        : !files.length
  ) {
    return null;
  }

  const handleSubmit = async () => {
    if (!selectedApprovers.length) {
      addNotification({
        type: "error",
        header: "Please select at least one approver",
        dismissible: true,
        timeout: 10000,
      });
      return;
    }

    if (!message.trim()) {
      addNotification({
        type: "error",
        header: "Please provide a message for reviewers",
        dismissible: true,
        timeout: 10000,
      });
      return;
    }

    try {
      const payload = isDeletion
        ? {
            teamName,
            category: "Deletion" as const,
            atlasObjectKeys,
            approvers: selectedApprovers.map((a) => a.value!),
            approvalMode,
            messageForApprovers: message.trim(),
            reviewTitle: reviewTitle.trim(),
          }
        : isExistingDoc
          ? {
              teamName,
              existingDocumentIds,
              approvers: selectedApprovers.map((a) => a.value!),
              approvalMode,
              messageForApprovers: message.trim(),
              reviewTitle: reviewTitle.trim(),
            }
          : {
              teamName,
              documents: files,
              approvers: selectedApprovers.map((a) => a.value!),
              approvalMode,
              messageForApprovers: message.trim(),
              reviewTitle: reviewTitle.trim(),
            };
      const result = await submitKBApproval(payload).unwrap();

      addNotification({
        type: "success",
        header: isDeletion
          ? "KB deletion approval submitted successfully"
          : "KB approval submitted successfully",
        dismissible: true,
        timeout: 10000,
      });
      navigate(
        result?.reviewId
          ? `/reviews/kb-approvals/${result.reviewId}`
          : "/reviews",
      );
    } catch (error: any) {
      addNotification({
        type: "error",
        header: isDeletion
          ? "Failed to submit deletion review"
          : "Failed to submit documents for review",
        content: error?.data?.message || "An unexpected error occurred.",
        dismissible: true,
        timeout: 10000,
      });
    }
  };

  const pageTitle = isDeletion
    ? "Submit documents for deletion review"
    : "Submit documents for review";

  return (
    <Page
      title={pageTitle}
      breadcrumbTitle={pageTitle}
      hasBreadcrumbs={true}
      homePage="/gaia-ai"
      type="page"
    >
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => navigate("/gaia-ai")}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={isSubmitting}
            >
              Submit
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween direction="vertical" size="l">
          <Container
            header={
              <Header variant="h2">
                {isDeletion
                  ? "Documents to delete"
                  : isExistingDoc
                    ? "Documents to submit"
                    : "Files"}
              </Header>
            }
          >
            {isDeletion ? (
              <Table
                columnDefinitions={[
                  {
                    id: "fileName",
                    header: "File name",
                    cell: (key: string) => getFilenameFromKey(key),
                    width: "50%",
                  },
                  {
                    id: "size",
                    header: "Size",
                    cell: (key: string) => fileSizes[key] || "-",
                    width: "30%",
                  },
                  {
                    id: "actions",
                    header: "Actions",
                    cell: (key: string) => (
                      <Button
                        variant="icon"
                        iconName="remove"
                        ariaLabel={`Remove ${getFilenameFromKey(key)}`}
                        onClick={() =>
                          setAtlasObjectKeys((prev) =>
                            prev.filter((k) => k !== key),
                          )
                        }
                      />
                    ),
                    width: "20%",
                  },
                ]}
                items={atlasObjectKeys}
                resizableColumns
                variant="embedded"
              />
            ) : isExistingDoc ? (
              <Table
                columnDefinitions={[
                  {
                    id: "fileName",
                    header: "File name",
                    cell: (docId: string) => fileNames[docId] || docId,
                    width: "70%",
                  },
                  {
                    id: "actions",
                    header: "Actions",
                    cell: (docId: string) => (
                      <Button
                        variant="icon"
                        iconName="remove"
                        ariaLabel={`Remove ${fileNames[docId] || docId}`}
                        onClick={() =>
                          setExistingDocumentIds((prev) =>
                            prev.filter((id) => id !== docId),
                          )
                        }
                      />
                    ),
                    width: "30%",
                  },
                ]}
                items={existingDocumentIds}
                resizableColumns
                variant="embedded"
              />
            ) : (
              <Table
                columnDefinitions={[
                  {
                    id: "fileName",
                    header: "File name",
                    cell: (item: KBDocumentInput) =>
                      `${item.fileName}${item.extensionType}`,
                    width: "50%",
                  },
                  {
                    id: "size",
                    header: "Size",
                    cell: (item: KBDocumentInput) => item.size,
                    width: "20%",
                  },
                  {
                    id: "type",
                    header: "Type",
                    cell: (item: KBDocumentInput) =>
                      item.replaceExisting ? (
                        <StatusIndicator type="info">
                          Replaces existing
                        </StatusIndicator>
                      ) : (
                        "New"
                      ),
                    width: "20%",
                  },
                  {
                    id: "actions",
                    header: "Actions",
                    cell: (item: KBDocumentInput) => (
                      <Button
                        variant="icon"
                        iconName="remove"
                        ariaLabel={`Remove ${item.fileName}${item.extensionType}`}
                        onClick={() =>
                          setFiles((prev) =>
                            prev.filter((f) => f.s3Key !== item.s3Key),
                          )
                        }
                      />
                    ),
                    width: "10%",
                  },
                ]}
                items={files}
                resizableColumns
                variant="embedded"
              />
            )}
          </Container>

          <Container header={<Header variant="h2">Approval details</Header>}>
            <SpaceBetween direction="vertical" size="l">
              <FormField label="Approval name">
                <Input
                  value={reviewTitle}
                  onChange={({ detail }) => setReviewTitle(detail.value)}
                  placeholder="Enter approval name"
                />
              </FormField>

              <Box>
                <ul>
                  <li>
                    Select one or more reviewers. Choose whether any single
                    reviewer can approve or all reviewers must approve.
                  </li>
                  <li>
                    {isDeletion
                      ? "Reviewers can approve or reject documents. Rejected documents will not be deleted."
                      : "Reviewers can approve or reject documents. Rejected documents are returned with a reason."}
                  </li>
                  <li>
                    {isDeletion
                      ? "Once final approval is reached, documents are automatically deleted from the Answers Knowledge Base."
                      : "Once final approval is reached, documents are automatically ingested into the Answers Knowledge Base."}
                  </li>
                </ul>
              </Box>

              <FormField
                label="Reviewers"
                description="Select one or more critical group members to review"
              >
                <Multiselect
                  selectedOptions={selectedApprovers}
                  onChange={({ detail }) =>
                    setSelectedApprovers([...detail.selectedOptions])
                  }
                  options={criticalOwnerOptions}
                  placeholder="Select reviewers"
                  filteringType="auto"
                />
              </FormField>

              <FormField label="Approval mode">
                <Select
                  selectedOption={
                    approvalMode === "AND"
                      ? { label: "All reviewers must approve", value: "AND" }
                      : { label: "Any reviewer can approve", value: "OR" }
                  }
                  onChange={({ detail }) =>
                    setApprovalMode(detail.selectedOption.value as "AND" | "OR")
                  }
                  options={[
                    { label: "All reviewers must approve", value: "AND" },
                    { label: "Any reviewer can approve", value: "OR" },
                  ]}
                  placeholder="Select condition"
                />
              </FormField>

              <FormField label="Message for reviewers">
                <Textarea
                  value={message}
                  onChange={({ detail }) => setMessage(detail.value)}
                  placeholder="Add a message for the reviewers..."
                />
              </FormField>
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </Form>
    </Page>
  );
};

export default ApproverSelectionPage;
